import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { CARD_TYPE, initialExp } from 'utils/validation'
import { companiesFields } from 'rest/urls'
import { STATUS } from 'utils/constants'
import { GET } from 'rest/request'

const fieldLabel = (name) => {
  switch (name) {
    case 'name': return { label: 'Nazwa', placeholder: 'Wpisz nazwę' }
    case 'streetName': return { label: 'Nazwa ulicy', placeholder: 'Wpisz nazwę ulicy' }
    case 'homeNr': return { label: 'Numer budynku', placeholder: 'Wpisz numer budynku' }
    case 'flatNr': return { label: 'Numer lokalu', placeholder: 'Wpisz numer lokalu' }
    case 'postalCode': return { label: 'Kod pocztowy', placeholder: 'Wpisz kod pocztowy' }
    case 'cityName': return { label: 'Miasto', placeholder: 'Wpisz miasto' }
    case 'voivodshipName': return { label: 'Województwo', placeholder: 'Wpisz województwo' }
    case 'country': return { label: 'Kraj', placeholder: 'Wpisz kraj' }
    case 'companyFormId': return { label: 'Rodzaj działalności', placeholder: 'Wpisz rodzaj działalności' }
    case 'companyUserPosition': return { label: 'Stanowisko', placeholder: 'Wpisz stanowisko' }
    default: return { label: name, placeholder: `Wpisz ${name}` }
  }
}

const withLabel = (field) => {
  if (field.inputLabel) return field
  const { label, placeholder } = fieldLabel(field.inputName)
  return { ...field, inputLabel: label, placeholder }
}

// Pola dopinane do formularza firmy, gdy wymaga ich walidacja koszyka, a nie ma ich w companiesFields
const FIELD_ANCHORS = { homeNr: 'streetName', flatNr: 'homeNr' }

/**
 * Pola formularza firmy = wymagane pola z companiesFields + pola wymagane przez koszyk
 * (`customerValidation.requiredInputs.company.requiredFields`), żeby dodana firma od razu
 * przechodziła walidację do umowy. Gdy jest numer budynku, pokazujemy też (opcjonalny) numer lokalu.
 */
export const getCompanyFormFields = (fields = [], allFields = [], spec) => {
  const required = (spec?.requiredFields || []).filter(name => name !== 'nip' && name !== 'companyUserId')
  if (required.length === 0) return fields

  const result = fields.map(f => required.includes(f.inputName) && !f.isRequired ? { ...f, isRequired: true } : f)
  const add = (name, isRequired) => {
    if (result.some(f => f.inputName === name)) return
    const base = allFields.find(f => f.inputName === name) || withLabel({ id: `cart-${name}`, inputName: name, inputType: 'text' })
    const field = { ...base, inputType: base.inputType === 'select' ? 'select' : 'text', isRequired }
    const anchorIdx = result.findIndex(f => f.inputName === FIELD_ANCHORS[name])
    if (anchorIdx >= 0) result.splice(anchorIdx + 1, 0, field)
    else result.push(field)
  }

  required.forEach(name => add(name, true))
  if (result.some(f => f.inputName === 'homeNr')) add('flatNr', false)
  return result
}

export const getCompaniesFields = createAsyncThunk('form/getCompaniesFields', async () => {
  const { fields = {} } = await GET(companiesFields)
  return fields
})

export const formSlice = createSlice({
  name: 'form',
  initialState: {
    data: {},
    fields: [],
    allFields: [],
    fieldsStatus: STATUS.idle,
  },
  reducers: {
    updateValue: (state, action) => {
      const { name, value } = action.payload
      const { cardType = CARD_TYPE.idCard } = state.data

      if (name) {
        if ((name === 'phone' || name === 'mail' || name === 'postalCode' || name === 'pesel') && value && !value.match(initialExp[name])) return
        else if (name === 'identityCard' && value && !value.match(initialExp[cardType])) return

        state.data[name] = value
      }
    },
    updateValues: (state, action) => {
      state.data = {
        ...state.data,
        ...action.payload
      }
    },
    resetForm: (state, _) => {
      state.data = {}
    },
  },
  extraReducers(builder) {
    builder.addCase(getCompaniesFields.fulfilled, (state, action) => {
      const { data } = action.payload

      if (Array.isArray(data)) {
        const allFields = data
          .filter(({ inputName }) => inputName !== 'nip' && inputName !== 'companyUserId')
          .map(withLabel)
        state.fieldsStatus = STATUS.succeed
        state.allFields = allFields
        state.fields = allFields.filter(({ isRequired }) => isRequired)
      }
    })
  }
})

export const { updateValue, updateValues, resetForm } = formSlice.actions

export const selectFieldsStatus = state => state.form.fieldsStatus
export const selectFields = state => state.form.fields
export const selectAllFields = state => state.form.allFields
export const selectForm = state => state.form.data

export default formSlice.reducer
